import { useState, type FormEvent, type JSX } from 'react';
import type { TFunction } from 'i18next';
import { useQuery } from '@tanstack/react-query';
import { needsUpdate, siteSchema, type Agent } from '@netdeck/contracts';
import { z } from 'zod';
import { apiRequest } from '../../shared/api/client';
import { useTranslation } from '../../shared/i18n';
import { useErrorText } from '../../shared/i18n/useErrorText';
import { Button } from '../../shared/ui/Button';
import { actionsWidth, DataTable, type Column } from '../../shared/ui/DataTable';
import { Field, FormActions, FormAlign, FormRow } from '../../shared/ui/Field';
import { InfoNote } from '../../shared/ui/InfoNote';
import { MultiSelect, type MultiSelectOption } from '../../shared/ui/MultiSelect';
import { EmptyState, ErrorState, Loading } from '../../shared/ui/Panel';
import { Sheet } from '../../shared/ui/Sheet';
import { StatusDot, type Status } from '../../shared/ui/StatusDot';
import { useIsPhone } from '../../shared/ui/viewport';
import { InstallPanel } from './InstallPanel';
import {
  useAgentRelease,
  useAgents,
  useCreateAgent,
  useDeleteAgent,
  useRotateToken,
  useUpdateAgent,
  useUpdateAgentNow,
} from './api';

/**
 * Экран «Площадки и агенты» — раздел 08 макета.
 *
 * Слева таблица агентов всех площадок, справа панель установки выбранного.
 * Столбец площадок — основной ориентир, а не служебная подробность: агент
 * существует ради них, а с ADR 032 их у него может быть несколько.
 */
export function AgentsPage({ canWrite }: { canWrite: boolean }): JSX.Element {
  const { t } = useTranslation();
  const errorText = useErrorText();

  const agents = useAgents();
  const createAgent = useCreateAgent();
  const rotateToken = useRotateToken();
  const deleteAgent = useDeleteAgent();
  const updateAgent = useUpdateAgent();
  const release = useAgentRelease();
  const updateNow = useUpdateAgentNow();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  /** Агент, который сейчас правится. Правка идёт формой над списком. */
  const [editing, setEditing] = useState<Agent | null>(null);
  /** Токен живёт только в состоянии экрана: показывается один раз. */
  const [issuedToken, setIssuedToken] = useState<{ agentId: string; token: string } | null>(null);

  const phone = useIsPhone();

  const rows = agents.data ?? [];
  const selected = rows.find((agent) => agent.id === selectedId) ?? rows[0] ?? null;

  /**
   * Агент, ради которого открыта шторка на телефоне.
   *
   * Не то же самое, что `selected`: колонке справа всегда есть что показать,
   * и первый агент выбирается за администратора. Шторка же поверх списка —
   * это ответ на нажатие, и открываться сама она не должна.
   */
  const opened = selectedId === null ? null : selected;

  /** Панель установки: одна и та же и в колонке, и в шторке телефона. */
  const installPanel = (agent: Agent): JSX.Element => (
    <InstallPanel
      agent={agent}
      enrollmentToken={
        issuedToken?.agentId === agent.id
          ? issuedToken.token
          : rotateToken.data && rotateToken.variables === agent.id
            ? rotateToken.data.enrollmentToken
            : null
      }
      rotating={rotateToken.isPending}
      onRotateToken={() => {
        if (confirm(t('agents.rotateConfirm', { name: agent.name }))) {
          rotateToken.mutate(agent.id);
        }
      }}
    />
  );

  const columns: Column<Agent>[] = [
    {
      key: 'status',
      header: '',
      width: '20px',
      // Отключённые наверх: сортируют этот столбец, чтобы найти площадку,
      // с которой пропала связь.
      sortValue: (agent) => AGENT_STATUS_ORDER[agentStatus(agent)],
      render: (agent) => <StatusDot status={agentStatus(agent)} label={statusLabel(agent, t)} />,
    },
    {
      key: 'name',
      header: t('agents.column.name'),
      width: '1fr',
      sortValue: (agent) => agent.name,
      /*
        `block` обязателен вместе с `truncate`: у строчного элемента
        обрезки не бывает — `overflow` к нему не применяется, и длинное
        имя не превращается в многоточие, а вылезает поверх соседней
        колонки. Ячейка таблицы ужаться ему позволяет (`min-w-0`),
        но обрезать содержимое должно оно само.
      */
      render: (agent) => <span className="block truncate font-mono">{agent.name}</span>,
    },
    {
      key: 'site',
      header: t('agents.column.sites'),
      width: '1fr',
      // На телефоне — во вторую строку карточки: агент существует ради
      // площадок, и без них его имя ни о чём не говорит.
      priority: 'summary',
      sortValue: (agent) => siteNames(agent),
      // Целиком — в подсказке: площадок у агента может быть больше, чем
      // помещается в колонку, а какие именно — это ровно то, ради чего
      // на эту колонку и смотрят.
      render: (agent) => (
        <span className="block truncate" title={siteNames(agent) || undefined}>
          {siteNames(agent) || <span className="text-dim">{t('agents.noSites')}</span>}
        </span>
      ),
    },
    {
      key: 'platform',
      header: t('agents.column.platform'),
      width: '0.8fr',
      sortValue: (agent) => agent.platform,
      render: (agent) => <span className="font-mono text-muted">{agent.platform ?? '—'}</span>,
    },
    {
      key: 'version',
      header: t('agents.column.version'),
      width: '90px',
      sortValue: (agent) => agent.version,
      render: (agent) => <span className="font-mono text-muted">{agent.version ?? '—'}</span>,
    },
    {
      key: 'update',
      header: t('agents.column.update'),
      width: '1fr',
      sortValue: (agent) => updateSortValue(agent, release.data?.version ?? null),
      render: (agent) => (
        <span
          className={`truncate font-mono text-xs ${agent.updateError === null ? 'text-dim' : 'text-down-fg'}`}
          title={agent.updateError ?? undefined}
        >
          {describeUpdate(agent, release.data?.version ?? null, t)}
        </span>
      ),
    },
    {
      key: 'lastSeen',
      header: t('agents.column.lastSeen'),
      width: '110px',
      /*
        Второе, что смотрят вместе с именем. Без разметки эту роль заняли бы
        платформа и версия — сведения для разбора, а не для взгляда: с экрана
        агентов уходят с ответом «когда пропала связь», а не «какая там
        архитектура».
      */
      priority: 'summary',
      sortValue: (agent) => agent.lastSeenAt,
      render: (agent) => (
        <span className={`font-mono ${agent.status === 'offline' ? 'text-down-fg' : 'text-muted'}`}>
          {lastSeen(agent, t)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      // Правка, обновление и удаление. Ширина считается, а не прикидывается:
      // ряд, не поместившийся в пиксельную колонку, вылезает из ячейки
      // и тянет горизонтальную прокрутку всей страницы.
      width: actionsWidth(3),
      align: 'right',
      // Иконками без подписей: подписанные кнопки распирали таблицу шире
      // экрана. Имя для скринридера и подсказка при наведении остаются.
      render: (agent) =>
        canWrite ? (
          <span className="inline-flex items-center justify-end gap-1">
            <Button
              icon="pencil"
              size="sm"
              variant="ghost"
              aria-label={t('agents.editAria', { name: agent.name })}
              title={t('agents.editTitle')}
              onClick={(event) => {
                event.stopPropagation();
                setAdding(false);
                setEditing(agent);
              }}
            />
            <Button
              icon="download"
              size="sm"
              variant="ghost"
              disabled={
                updateNow.isPending ||
                agent.status !== 'online' ||
                !isOutdated(agent, release.data?.version ?? null)
              }
              aria-label={t('agents.updateAria', { name: agent.name })}
              title={updateNowTitle(agent, release.data?.version ?? null, t)}
              onClick={(event) => {
                event.stopPropagation();
                updateNow.mutate(agent.id);
              }}
            />
            <Button
              icon="trash"
              size="sm"
              variant="danger"
              disabled={deleteAgent.isPending}
              aria-label={t('agents.deleteAria', { name: agent.name })}
              title={t('agents.deleteTitle')}
              onClick={(event) => {
                event.stopPropagation();
                if (confirm(t('agents.deleteConfirm', { name: agent.name }))) {
                  deleteAgent.mutate(agent.id);
                }
              }}
            />
          </span>
        ) : (
          <span className="font-mono text-xs text-faint">—</span>
        ),
    },
  ];

  return (
    <div className="flex min-h-0 flex-1 flex-col p-4">
      <div className="grid min-h-0 flex-1 grid-cols-1 overflow-hidden rounded-lg border border-line bg-surface xl:grid-cols-[1fr_400px]">
        <div className="flex min-h-0 flex-col border-line xl:border-r">
          <header className="flex h-11 flex-none items-center gap-2.5 border-b border-line bg-raised px-3.5">
            <h2 className="flex-none text-lg font-semibold">{t('nav.agents')}</h2>
            <span className="flex-none font-mono text-xs text-dim">{rows.length}</span>

            {/*
              Два пояснения — как агент соотносится с площадками и состояние
              каталога сборок — уехали под значок. В строке они занимали больше места, чем
              заголовок с кнопкой вместе, и на телефоне выдавливали кнопку
              «Добавить агента» за экран. Спрашивают о них не каждый день,
              а кнопка нужна сразу.
            */}
            <InfoNote label={t('agents.info.label')}>
              <p>{t('agents.info.onePerSite')}</p>
              <p className="mt-2">
                {release.data?.available
                  ? t('agents.info.catalogAvailable', {
                      version: release.data.version ?? t('common.dash'),
                    })
                  : t('agents.info.catalogMissing')}
              </p>
              {release.data?.problem === undefined || release.data.problem === null ? null : (
                <p className="mt-2 text-warn">{release.data.problem}</p>
              )}
              {release.data?.notes ? <p className="mt-2 text-dim">{release.data.notes}</p> : null}
            </InfoNote>

            {canWrite ? (
              <Button
                icon={adding ? 'close' : 'plus'}
                size="sm"
                className="ml-auto"
                onClick={() => {
                  setEditing(null);
                  setAdding((value) => !value);
                }}
              >
                {adding ? t('common.action.cancel') : t('agents.add')}
              </Button>
            ) : null}
          </header>

          {editing ? (
            <EditAgentForm
              key={editing.id}
              agent={editing}
              agents={agents.data ?? []}
              pending={updateAgent.isPending}
              error={updateAgent.error}
              onCancel={() => setEditing(null)}
              onSubmit={(input) =>
                updateAgent.mutate(
                  { agentId: editing.id, input },
                  { onSuccess: () => setEditing(null) },
                )
              }
            />
          ) : null}

          {adding ? (
            <AddAgentForm
              agents={agents.data ?? []}
              pending={createAgent.isPending}
              error={createAgent.error}
              onSubmit={(siteIds, name) =>
                createAgent.mutate(
                  { siteIds, name },
                  {
                    onSuccess: (result) => {
                      setAdding(false);
                      setSelectedId(result.agent.id);
                      setIssuedToken({
                        agentId: result.agent.id,
                        token: result.enrollmentToken,
                      });
                    },
                  },
                )
              }
            />
          ) : null}

          <div className="min-h-0 flex-1 overflow-auto">
            {agents.isPending ? (
              <Loading />
            ) : agents.error ? (
              <ErrorState
                title={t('agents.loadFailed')}
                hint={t('agents.loadFailedHint', { reason: errorText(agents.error) })}
              />
            ) : (
              <DataTable
                columns={columns}
                rows={rows}
                rowKey={(agent) => agent.id}
                rowDanger={(agent) => agent.status === 'offline'}
                onRowClick={(agent) => setSelectedId(agent.id)}
                selectedKey={selected?.id}
                empty={
                  <EmptyState
                    title={t('agents.emptyTitle')}
                    hint={t('agents.emptyHint')}
                    action={
                      canWrite ? (
                        <Button icon="plus" size="sm" onClick={() => setAdding(true)}>
                          {t('agents.addFirst')}
                        </Button>
                      ) : null
                    }
                  />
                }
              />
            )}
          </div>
        </div>

        {/*
          На телефоне панель приходит шторкой поверх списка, а не второй
          строкой под ним: команда установки со всеми её кнопками и суммами
          длиннее экрана, и список агентов под ней было не найти.
        */}
        {phone ? null : selected ? (
          installPanel(selected)
        ) : (
          <div className="hidden p-3.5 text-sm text-muted xl:block">{t('agents.pickAgent')}</div>
        )}
      </div>

      {phone && opened ? (
        <Sheet title={opened.name} onClose={() => setSelectedId(null)}>
          {installPanel(opened)}
        </Sheet>
      ) : null}
    </div>
  );
}

/**
 * Правка агента: название и площадка.
 *
 * Перенос на другую площадку меняет не подпись, а обязанности — агент
 * раздаёт мониторы своей площадки и служит маршрутом к её узлам, — поэтому
 * форма об этом предупреждает. Соединение при переносе не рвётся: сервер
 * переносит его вместе с записью и присылает агенту задачи новой площадки.
 */
/**
 * Что показывать в столбце обновления.
 *
 * Три разных состояния, и путать их нельзя: неудачная попытка — это красное
 * и требует внимания; «отстал» — повод нажать кнопку; выключенное
 * автообновление — осознанное решение администратора, а не проблема.
 */
export function describeUpdate(
  agent: Pick<Agent, 'version' | 'autoUpdate' | 'updateVersion' | 'updateError'>,
  releaseVersion: string | null,
  t: TFunction,
): string {
  if (agent.updateError !== null) {
    return t('agents.update.failed', { version: agent.updateVersion ?? '?' });
  }

  if (releaseVersion === null) {
    return agent.autoUpdate ? t('common.dash') : t('agents.update.disabled');
  }

  if (isOutdated(agent, releaseVersion)) {
    return agent.autoUpdate
      ? t('agents.update.behind', { version: releaseVersion })
      : t('agents.update.behindDisabled', { version: releaseVersion });
  }

  return agent.autoUpdate ? t('agents.update.current') : t('agents.update.currentDisabled');
}

/** Отстал ли агент от выпуска. */
export function isOutdated(agent: Pick<Agent, 'version'>, releaseVersion: string | null): boolean {
  return releaseVersion !== null && needsUpdate(agent.version, releaseVersion);
}

/**
 * Порядок сортировки столбца: сперва неудачи, затем отставшие.
 *
 * Сортируют этот столбец ровно затем, чтобы найти площадку, на которой
 * обновление не прошло, — и она должна оказаться сверху.
 */
function updateSortValue(agent: Agent, releaseVersion: string | null): number {
  if (agent.updateError !== null) {
    return 0;
  }
  if (isOutdated(agent, releaseVersion)) {
    return 1;
  }
  return agent.autoUpdate ? 3 : 2;
}

function updateNowTitle(agent: Agent, releaseVersion: string | null, t: TFunction): string {
  if (agent.status !== 'online') {
    return t('agents.updateNow.offline');
  }
  if (releaseVersion === null) {
    return t('agents.updateNow.noCatalog');
  }
  if (!isOutdated(agent, releaseVersion)) {
    return t('agents.updateNow.upToDate');
  }
  return t('agents.updateNow.action', { version: releaseVersion });
}

function EditAgentForm({
  agent,
  agents,
  pending,
  error,
  onCancel,
  onSubmit,
}: {
  agent: Agent;
  /** Остальные агенты — чтобы показать, какие площадки уже заняты. */
  agents: Agent[];
  pending: boolean;
  error: Error | null;
  onCancel: () => void;
  onSubmit: (input: { name?: string; siteIds?: string[]; autoUpdate?: boolean }) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const errorText = useErrorText();

  const [siteIds, setSiteIds] = useState(() => agent.sites.map((site) => site.id));
  const [name, setName] = useState(agent.name);
  const [autoUpdate, setAutoUpdate] = useState(agent.autoUpdate);

  const options = useSiteOptions(agent, agents);

  const moving = !sameSiteIds(
    siteIds,
    agent.sites.map((site) => site.id),
  );

  function submit(event: FormEvent): void {
    event.preventDefault();

    onSubmit({
      ...(name.trim() !== agent.name ? { name: name.trim() } : {}),
      ...(moving ? { siteIds } : {}),
      ...(autoUpdate !== agent.autoUpdate ? { autoUpdate } : {}),
    });
  }

  return (
    <FormRow onSubmit={submit}>
      <div className="w-full sm:w-[220px]">
        <MultiSelect
          label={t('agents.form.sites')}
          options={options}
          value={siteIds}
          onChange={setSiteIds}
          placeholder={t('agents.form.pickSites')}
          empty={t('agents.form.noSitesYet')}
        />
      </div>

      <div className="w-full sm:w-[220px]">
        <Field
          label={t('agents.form.name')}
          required
          mono
          value={name}
          onChange={(event) => setName(event.target.value)}
          hint={t('agents.form.nameHint')}
        />
      </div>

      <FormAlign title={t('agents.form.autoUpdateHint')}>
        <label className="flex items-center gap-1.5 text-sm">
          <input
            type="checkbox"
            checked={autoUpdate}
            onChange={(event) => setAutoUpdate(event.target.checked)}
          />
          {t('agents.form.autoUpdate')}
        </label>
      </FormAlign>

      <FormActions>
        <Button
          icon="check"
          type="submit"
          variant="primary"
          size="sm"
          disabled={
            pending ||
            !name.trim() ||
            (!moving && name.trim() === agent.name && autoUpdate === agent.autoUpdate)
          }
        >
          {pending ? t('sites.form.saving') : t('common.action.save')}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          {t('common.action.cancel')}
        </Button>
      </FormActions>

      {/*
        Предупреждение о переезде и отказ — на своей строке, а не рядом
        с кнопками: в ряду высотой с поле ввода фраза в полторы строки
        либо обрезается, либо распирает ряд и уводит кнопки с общей линии.
        `basis-full` — перенос внутри той же обёртки с переносом.
      */}
      {moving || error ? (
        <p className={`basis-full text-sm ${error ? 'text-down-fg' : 'text-muted'}`}>
          {error ? errorText(error) : t('agents.form.movingNote')}
        </p>
      ) : null}
    </FormRow>
  );
}

function AddAgentForm({
  agents,
  pending,
  error,
  onSubmit,
}: {
  /** Уже заведённые агенты — чтобы не предлагать занятую ими площадку. */
  agents: Agent[];
  pending: boolean;
  error: Error | null;
  onSubmit: (siteIds: string[], name: string) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const errorText = useErrorText();

  const [siteIds, setSiteIds] = useState<string[]>([]);
  const [name, setName] = useState('');

  const options = useSiteOptions(null, agents);

  function submit(event: FormEvent): void {
    event.preventDefault();
    onSubmit(siteIds, name.trim());
  }

  return (
    <FormRow onSubmit={submit}>
      {/*
        Площадок сразу несколько (ADR 033): один сегмент сети, поделённый
        на склад и офис, обслуживается одним агентом, и заводить его на одной
        площадке, чтобы тут же открыть правку и отметить вторую, незачем.
      */}
      <div className="w-full sm:w-[220px]">
        <MultiSelect
          label={t('agents.form.sites')}
          options={options}
          value={siteIds}
          onChange={setSiteIds}
          placeholder={t('agents.form.pickSites')}
          hint={t('agents.form.sitesHintNew')}
          empty={t('agents.form.noSitesYet')}
        />
      </div>

      <div className="w-full sm:w-[220px]">
        <Field
          label={t('agents.form.name')}
          required
          mono
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="A-01"
          hint={t('agents.form.nameHintNew')}
        />
      </div>

      <FormActions>
        <Button
          icon="plus"
          type="submit"
          variant="primary"
          size="sm"
          disabled={pending || !name.trim()}
        >
          {pending ? t('sites.form.creating') : t('sites.form.create')}
        </Button>
      </FormActions>

      {error ? <p className="basis-full text-sm text-down-fg">{errorText(error)}</p> : null}
    </FormRow>
  );
}

/**
 * Площадки как пункты списка с галочками.
 *
 * Занятая площадка не исчезает, а показывается выключенной с именем хозяина:
 * пропавшую из списка человек стал бы искать среди площадок, а видя имя, он
 * сразу знает, с кого её снимать. Своя площадка правящегося агента занятой
 * не считается — иначе снять с неё галочку было бы нечем.
 *
 * Пока список площадок не пришёл, пунктами служат площадки самого агента:
 * иначе форма правки первым кадром говорила бы «выберите площадки» про
 * агента, у которого они есть, — то есть про ровно то, что человек открыл
 * менять.
 */
function useSiteOptions(editing: Agent | null, agents: Agent[]): MultiSelectOption[] {
  const { t } = useTranslation();

  const sites = useQuery({
    queryKey: ['sites'],
    queryFn: () => apiRequest('/sites', z.array(siteSchema)),
  });

  const takenBy = new Map<string, string>();
  for (const agent of agents) {
    if (agent.id === editing?.id) {
      continue;
    }

    for (const site of agent.sites) {
      takenBy.set(site.id, agent.name);
    }
  }

  return (sites.data ?? editing?.sites ?? []).map((site) => {
    const owner = takenBy.get(site.id);

    return {
      value: site.id,
      label: site.name,
      disabledReason: owner === undefined ? undefined : t('agents.form.siteTaken', { name: owner }),
    };
  });
}

/**
 * §12: статус кодируется формой, а не только цветом.
 * Ожидающий регистрации агент показывается полым кругом — он ещё не
 * «не работает», он просто ни разу не подключался.
 */
/** Площадки агента одной строкой. Пусто — агент ни к чему не привязан. */
function siteNames(agent: Agent): string {
  return agent.sites.map((site) => site.name).join(', ');
}

/** Тот же набор площадок, без учёта порядка: галочки ставят вразнобой. */
function sameSiteIds(left: readonly string[], right: readonly string[]): boolean {
  if (left.length !== right.length) {
    return false;
  }

  const known = new Set(left);
  return right.every((id) => known.has(id));
}

function agentStatus(agent: Agent): Status {
  switch (agent.status) {
    case 'online':
      return 'up';
    case 'offline':
      return 'down';
    default:
      return 'unknown';
  }
}

function statusLabel(agent: Agent, t: TFunction): string {
  switch (agent.status) {
    case 'online':
      return t('agents.status.online');
    case 'offline':
      return t('agents.status.offline');
    default:
      return t('agents.status.never');
  }
}

function lastSeen(agent: Agent, t: TFunction): string {
  if (!agent.lastSeenAt) {
    return t('agents.lastSeen.never');
  }

  const seconds = Math.max(0, Math.round((Date.now() - Date.parse(agent.lastSeenAt)) / 1000));

  // Подключённый агент отмечается в БД каждые полминуты (ADR 034), поэтому
  // число секунд у него теперь есть всегда — и дёргалось бы в столбце,
  // ничего не сообщая: «на связи» и есть весь ответ.
  if (agent.status === 'online') {
    return t('agents.lastSeen.live');
  }
  if (seconds < 60) {
    return t('common.ago.seconds', { count: seconds });
  }
  if (seconds < 3600) {
    return t('common.ago.minutes', { count: Math.round(seconds / 60) });
  }
  if (seconds < 86_400) {
    return t('common.ago.hours', { count: Math.round(seconds / 3600) });
  }
  return t('common.ago.days', { count: Math.round(seconds / 86_400) });
}

/**
 * Порядок состояний агента при сортировке: сперва то, с чем надо разбираться.
 *
 * По алфавиту он был бы бессмысленным: сортируют этот столбец, чтобы найти
 * площадку, с которой пропала связь, — а не чтобы собрать буквы по порядку.
 */
const AGENT_STATUS_ORDER: Record<Status, number> = {
  down: 0,
  warn: 1,
  unknown: 2,
  up: 3,
};
